'use client';

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSession } from 'next-auth/react';
import { Heart } from 'lucide-react';
import { useSiteConfig } from '@/core/components/providers/SiteConfigProvider';
import { useTranslation } from '@/core/components/providers/TranslationProvider';
import { LoginGateModal } from '@/modules/shop/components/negotiation/login-gate-modal';
import { toast } from '@/core/utils/toast';
import { cn } from '@/core/utils/cn';
import { loadMine, requestCount, resetMine, toggleFavorite, useWishlistEntry } from './wishlist-store';

interface WishlistHeartProps {
    productId: number;
    /** card — product grid tile; gallery — the product page's main photo (a bit larger). */
    variant?: 'card' | 'gallery';
    /** Builder/Studio preview: shown as it will look, but no requests and no clicks. */
    preview?: boolean;
    className?: string;
}

/**
 * The heart on a product photo (Integracje → Ulubione). Renders nothing unless the shop switched
 * the integration on. Guests get the "create an account" prompt — collecting accounts is what the
 * merchant wants from it; the count shows how many customers hearted the product.
 *
 * It sits inside the product card's link and the gallery's zoom area, so its own clicks stop there,
 * and the prompt is portalled to <body>: the card can be transformed on hover (a `fixed` modal
 * inside it would be positioned against the card) and a click inside it must not open the product.
 */
export function WishlistHeart({ productId, variant = 'card', preview = false, className }: WishlistHeartProps) {
    const siteConfig = useSiteConfig();
    const { t } = useTranslation();
    const { data: session, status } = useSession();
    const { favorited, count } = useWishlistEntry(productId);
    const [gateOpen, setGateOpen] = useState(false);
    const [busy, setBusy] = useState(false);

    const enabled = !!siteConfig?.wishlist_enabled && !!productId;
    const showCount = enabled && siteConfig?.wishlist_show_counts !== false;
    const live = enabled && !preview;
    const userKey = (session?.user as any)?.email || (session?.user as any)?.id || '';

    useEffect(() => {
        if (live && showCount) requestCount(productId);
    }, [live, showCount, productId]);

    useEffect(() => {
        if (!live) return;
        if (status === 'authenticated' && userKey) loadMine(String(userKey));
        else if (status === 'unauthenticated') resetMine();
    }, [live, status, userKey]);

    if (!enabled) return null;

    const onClick = async (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (preview || busy) return;
        if (status !== 'authenticated') {
            setGateOpen(true);
            return;
        }
        setBusy(true);
        try {
            const nowFavorite = await toggleFavorite(productId);
            if (nowFavorite) {
                toast.success(siteConfig?.wishlist_price_alerts !== false
                    ? t('wishlist.added_toast_alerts', 'Dodano do ulubionych. Damy Ci znać e-mailem, gdy cena spadnie.')
                    : t('wishlist.added_toast', 'Dodano do ulubionych'));
            } else {
                toast.success(t('wishlist.removed_toast', 'Usunięto z ulubionych'));
            }
        } catch {
            toast.error(t('wishlist.error', 'Nie udało się zapisać ulubionych. Spróbuj ponownie.'));
        } finally {
            setBusy(false);
        }
    };

    const visibleCount = showCount && !preview && count && count > 0 ? count : null;
    const label = favorited ? t('wishlist.remove', 'Usuń z ulubionych') : t('wishlist.add', 'Dodaj do ulubionych');

    return (
        <>
            <button
                type="button"
                onClick={onClick}
                aria-pressed={favorited}
                aria-label={visibleCount ? `${label} (${t('wishlist.count_label', 'Polubienia: {count}').replace('{count}', String(visibleCount))})` : label}
                title={label}
                className={cn(
                    'absolute z-10 inline-flex items-center gap-1 rounded-full bg-white/90 text-slate-700 shadow-sm ring-1 ring-black/5 backdrop-blur-sm transition-transform hover:scale-105 active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
                    variant === 'gallery' ? 'top-3 right-3 h-10 min-w-10 px-2.5' : 'top-2 right-2 h-8 min-w-8 px-2',
                    visibleCount ? 'justify-start' : 'justify-center',
                    busy && 'opacity-70',
                    className,
                )}
            >
                <Heart
                    aria-hidden
                    className={cn(
                        'shrink-0 transition-colors',
                        variant === 'gallery' ? 'h-5 w-5' : 'h-4 w-4',
                        favorited ? 'fill-red-500 text-red-500' : 'text-slate-700',
                    )}
                />
                {visibleCount && (
                    <span className={cn('font-semibold tabular-nums leading-none', variant === 'gallery' ? 'text-sm' : 'text-xs')}>
                        {visibleCount}
                    </span>
                )}
            </button>
            {gateOpen && typeof document !== 'undefined' && createPortal(
                <div onClick={(e) => e.stopPropagation()}>
                    <LoginGateModal
                        isOpen
                        onClose={() => setGateOpen(false)}
                        title={t('wishlist.login_gate_title', 'Zapisuj ulubione')}
                        message={t('wishlist.login_gate_message', 'Załóż bezpłatne konto lub zaloguj się, żeby zapisywać ulubione produkty — damy Ci znać, gdy ich cena spadnie.')}
                    />
                </div>,
                document.body,
            )}
        </>
    );
}
